import { Suspense } from 'react';
import { QueryClient } from '@tanstack/react-query';
import withLazyLoading from '../../hoc/withLazyLoading';
import { createCrumbHandle } from '../../utilities/auth';
import withPostHogTracker from '../../composeRouter';
import Loader from '../../globalComponents/loader/DPLoader';
import { scadaLoader } from '../../pages/visualization/scadaConfigurationModule/scadaLiveSimulation/loader';
import { SCADA_RULES, access } from '../../access';

const AssetConfiguration = withLazyLoading(
  () => import('../../pages/visualization/assetConfiguration')
);
const ScadaLiveSimulation = withLazyLoading(
  () => import('../../pages/visualization/scadaConfigurationModule/scadaLiveSimulation')
);
const LayoutVisualisationConfig = withLazyLoading(
  () => import('../../pages/LayoutVisualisationConfig')
);
const CreateAssets = withLazyLoading(
  () => import('../../pages/createAssets')
);
const AssetSurvey = withLazyLoading(
  () => import('../../pages/createAssets/AssetSurvey')
);
const PageListing = withLazyLoading(
  () => import('../../pages/visualization/visualizationPages/pageListing')
);
const PagesConfiguration = withLazyLoading(
  () => import('../../pages/visualization/visualizationPages/pagesConfiguration')
);
const ScadaView = withLazyLoading(
  () => import('../../pages/visualization/scadaConfigurationModule/scadaLiveSimulation/scadaView')
);
const TemplateListing = withLazyLoading(
  () => import('../../pages/visualization/visualizationTemplates/templateListing')
);
const TemplateConfiguration = withLazyLoading(
  () => import('../../pages/visualization/visualizationTemplates/templateConfiguration')
);

const visualizationRoutes = (queryClient: QueryClient) => [
  {
    path: '/visualization-configuration/:workspaceId/:assetId?/:id?/*',
    element: <AssetConfiguration />,
    handle: { ...createCrumbHandle('Asset Configuration'), ...access(SCADA_RULES.edit) },
  },
  {
    path: '/plant-view/:workspaceId',
    element: withPostHogTracker(<ScadaLiveSimulation />),
    handle: access(SCADA_RULES.view),
    loader: scadaLoader(queryClient),
    children: [
      {
        path: ':assetId?/*',
        index: true,
        handle: access(SCADA_RULES.view),
        element: (
          <Suspense fallback={<Loader />}>
            <ScadaView />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/asset-creation/:id?',
    element: <CreateAssets />,
    handle: { ...createCrumbHandle('Create Asset'), ...access(SCADA_RULES.author) },
  },
  {
    path: '/asset-survey/:workspaceId/:assetId?',
    element: <AssetSurvey />,
    handle: { ...createCrumbHandle('Asset Survey'), ...access(SCADA_RULES.author) },
  },
  {
    path: '/LayoutVisualisationConfig/:workspaceId',
    element: <LayoutVisualisationConfig />,
    handle: { ...createCrumbHandle('Asset Configuration'), ...access(SCADA_RULES.author) },
  },
  {
    path: '/pages/:workspaceId',
    handle: createCrumbHandle('Pages'),
    children: [
      {
        index: true,
        handle: access(SCADA_RULES.author),
        element: (
          <Suspense fallback={<Loader />}>
            <PageListing />
          </Suspense>
        ),
      },
      {
        path: ':pageId',
        element: <PagesConfiguration />,
        handle: { ...createCrumbHandle('Configuration'), ...access(SCADA_RULES.edit) },
      },
    ]
  },
  {
    path: '/visualization-templates',
    handle: createCrumbHandle('Templates'),
    children: [
      {
        index: true,
        handle: access(SCADA_RULES.author),
        element: (
          <Suspense fallback={<Loader />}>
            <TemplateListing />
          </Suspense>
        ),
      },
      {
        path: 'new-template',
        element: <TemplateConfiguration />,
        handle: { ...createCrumbHandle('New Template'), ...access(SCADA_RULES.edit) },
      },
      {
        path: ':templateId',
        element: <TemplateConfiguration />,
        handle: { ...createCrumbHandle('Edit Template'), ...access(SCADA_RULES.edit) },
      },
    ]
  },
];

export default visualizationRoutes;
